// modules
import React, { lazy, Suspense, useState, useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import queryString from 'query-string';

// logic
import { useStore, selectedApp } from '../../store';
import { updateUIState } from '../../~reusables/actions';
import { track } from '../../~reusables/util/analytics';
import { products } from '@todesktop/shared';
import { stage } from '../../~reusables/firebase/config';

// Each modal only shows for its uiState, so it loads when first shown rather
// than with every page
const ManageSubscriptions = lazy(
  () => import('../account/ManageSubscriptions'),
);
const ManageAccount = lazy(() => import('../account/ManageAccount'));
const ChoosePackage = lazy(() => import('../build/ChoosePackage'));
const CreateSubWithoutBuild = lazy(() =>
  import('../account/CreateAccount').then((m) => ({
    default: m.CreateSubWithoutBuild,
  })),
);
const BillingAccountPicker = lazy(() =>
  import('../billing/BillingAccountPicker').then((m) => ({
    default: m.BillingAccountPicker,
  })),
);
const LegacyPayModal = lazy(() => import('../build/LegacyPayModal'));
const PayToDesktopBuilder = lazy(() =>
  import('../build/PayToDesktopBuilder').then((m) => ({
    default: m.PayToDesktopBuilder,
  })),
);
const SubscribeModal = lazy(() =>
  import('../build/SubscribeModal').then((m) => ({
    default: m.SubscribeModal,
  })),
);
const CreateAppModal = lazy(() =>
  import('../modals/CreateAppModal').then((m) => ({
    default: m.CreateAppModal,
  })),
);
const ManageSecurityToken = lazy(() => import('../modals/ManageSecurityToken'));
const AdminLogin = lazy(() =>
  import('../build/AdminLogin').then((m) => ({ default: m.AdminLogin })),
);

type IModalsContainer = RouteComponentProps;

const ModalsContainer: React.FC<IModalsContainer> = (routeProps) => {
  const { location } = routeProps;
  const [priceId, setPriceIdDirect] = useState(
    products[stage].legacy.professional.prices.monthly_199.id,
  );
  const [billingPickerVisible, setBillingPickerVisible] = useState(false);
  // Stays mounted after closing so the modal can animate out
  const [hasOpenedBillingPicker, setHasOpenedBillingPicker] = useState(false);
  if (billingPickerVisible && !hasOpenedBillingPicker) {
    setHasOpenedBillingPicker(true);
  }
  const { uiState, user } = useStore((state) => ({
    uiState: state.uiState,
    user: state.user,
    selectedTeam: state.selectedTeam,
  }));

  const app = useStore(selectedApp);

  const setPriceId = (plan: string) => () => {
    setPriceIdDirect(plan);
    updateUIState('pay');
    track({ event: 'Selected Plan', properties: { plan } });
  };

  useEffect(() => {
    const query = queryString.parse(location.search);
    if (user) {
      if (query['uistate'] === 'manage-billing') {
        /**
         * Allow user to manage billing if their search query contains
         * "/?uistate=manage-billing". The BillingAccountPicker handles
         * checking permissions and available billing accounts.
         */
        setBillingPickerVisible(true);
      } else if (query['uistate'] === 'manage-users') {
        // Redirect to dedicated account page, preserving id param for invite acceptance
        const idParam =
          typeof query['id'] === 'string'
            ? `?${queryString.stringify({ id: query['id'] })}`
            : '';
        window.location.href = `/settings/users${idParam}`;
      } else if (query['uistate'] === 'manage-access-token') {
        window.location.href = '/settings/access-tokens';
      }
    }
  }, [location.search, user]);

  return (
    <Suspense fallback={null}>
      {uiState === 'choose-plan' ? (
        <ChoosePackage setPriceId={setPriceId} />
      ) : null}
      {uiState === 'pay' && app ? (
        <LegacyPayModal app={app} priceId={priceId} />
      ) : null}
      {uiState === 'subscribe' ? <SubscribeModal /> : null}
      {uiState === 'pay-todesktop-builder' ? <PayToDesktopBuilder /> : null}
      {uiState === 'create-sub-without-build' && app ? (
        <CreateSubWithoutBuild app={app} />
      ) : null}
      {uiState === 'create-new-app' ? <CreateAppModal /> : null}
      {uiState === 'manage-subscriptions' ? <ManageSubscriptions /> : null}
      {uiState === 'manage-account' ? <ManageAccount /> : null}
      {uiState === 'manage-security-token' ? <ManageSecurityToken /> : null}
      {uiState === 'admin-login' ? <AdminLogin /> : null}
      {hasOpenedBillingPicker ? (
        <BillingAccountPicker
          visible={billingPickerVisible}
          onClose={() => setBillingPickerVisible(false)}
        />
      ) : null}
    </Suspense>
  );
};

export default ModalsContainer;
